import React, { createContext, useContext, useMemo, useState } from "react";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { Cookie, X, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";

const CookieCtx = createContext(null);
export const useCookieCtx = () => useContext(CookieCtx);

export function CookieProvider({ children }) {
  const consent = useCookieConsent();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const value = useMemo(() => ({ ...consent, openSettings: () => setSettingsOpen(true) }), [consent]);

  return (
    <CookieCtx.Provider value={value}>
      {children}
      <CookieBanner />
      <CookieSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </CookieCtx.Provider>
  );
}

function CookieBanner() {
  const ctx = useCookieCtx();
  const [showSettings, setShowSettings] = useState(false);
  if (!ctx) return null;
  if (ctx.consent.decided) return null;

  return (
    <>
      <div
        data-testid="cookie-banner"
        className="fixed inset-x-3 bottom-3 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[70] rounded-2xl border border-amber-300/30 bg-[#0A0D17]/95 backdrop-blur-xl p-4 sm:p-5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)] text-slate-200"
        role="dialog"
        aria-labelledby="cookie-banner-title"
      >
        <button
          type="button"
          onClick={() => ctx.rejectOptional()}
          data-testid="cookie-quick-close"
          aria-label="Zavřít a odmítnout volitelné cookies"
          className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white/5 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-8">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-300/10 border border-amber-300/20 text-amber-300">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div id="cookie-banner-title" className="font-heading text-sm sm:text-base font-bold text-white">
              Cookies a soukromí
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-300">
              Technické cookies jsou nezbytné. Analytické a marketingové použijeme jen s vaším souhlasem.{" "}
              <Link to="/cookies" className="text-amber-300 hover:underline">
                Zásady
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => ctx.acceptAll()}
            data-testid="cookie-accept-all-btn"
            className="flex-1 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 transition-all"
          >
            Přijmout vše
          </button>
          <button
            type="button"
            onClick={() => ctx.rejectOptional()}
            data-testid="cookie-reject-btn"
            className="flex-1 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-4 py-2.5 text-sm font-semibold text-white transition-colors"
          >
            Odmítnout volitelné
          </button>
        </div>
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          data-testid="cookie-settings-btn"
          className="mt-1.5 w-full text-center text-xs text-amber-300 hover:text-amber-200"
        >
          Nastavit cookies
        </button>
      </div>

      <CookieSettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </>
  );
}

function CookieSettingsModal({ open, onClose }) {
  const ctx = useCookieCtx();
  // derive initial values when modal opens; key on open+consent so state resets on each open
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [lastOpen, setLastOpen] = useState(false);

  if (open && !lastOpen) {
    setLastOpen(true);
    setAnalytics(ctx?.consent?.analytics || false);
    setMarketing(ctx?.consent?.marketing || false);
  }
  if (!open && lastOpen) {
    setLastOpen(false);
  }

  if (!ctx || !open) return null;

  return (
    <div
      data-testid="cookie-settings-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-amber-300/25 bg-[#0A0D17] text-slate-200 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="font-heading text-lg font-bold text-white">Nastavení cookies</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="cookie-settings-close"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-white/5 hover:text-white"
            aria-label="Zavřít"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <CookieRow
            icon={<ShieldCheck className="h-4 w-4" />}
            title="Technické cookies"
            text="Tyto cookies jsou nezbytné pro správné fungování webu, bezpečnost, odeslání formuláře, uložení nastavení cookies a základní technické funkce. Nelze je vypnout."
            checked
            disabled
          />
          <CookieRow
            icon={<BarChart3 className="h-4 w-4" />}
            title="Analytické cookies"
            text="Pomáhají nám porozumět návštěvnosti webu, měřit kliknutí na telefon, formulář a důležité prvky. Používáme je pouze na základě vašeho souhlasu."
            checked={analytics}
            onChange={setAnalytics}
            testid="cookie-row-analytics"
          />
          <CookieRow
            icon={<Megaphone className="h-4 w-4" />}
            title="Marketingové cookies"
            text="Pomáhají nám měřit účinnost reklamy a zobrazovat relevantnější nabídky. Používáme je pouze na základě vašeho souhlasu."
            checked={marketing}
            onChange={setMarketing}
            testid="cookie-row-marketing"
          />
        </div>

        <div className="border-t border-white/10 px-5 py-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => { ctx.saveCustom({ analytics, marketing }); onClose(); }}
            data-testid="cookie-save-btn"
            className="flex-1 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900"
          >
            Uložit nastavení
          </button>
          <button
            type="button"
            onClick={() => { ctx.acceptAll(); onClose(); }}
            data-testid="cookie-modal-accept-all"
            className="rounded-lg border border-amber-300/30 bg-amber-300/10 hover:bg-amber-300/20 px-4 py-2.5 text-sm font-semibold text-amber-200"
          >
            Přijmout vše
          </button>
          <button
            type="button"
            onClick={() => { ctx.rejectOptional(); onClose(); }}
            data-testid="cookie-modal-reject"
            className="rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-4 py-2.5 text-sm font-semibold text-white"
          >
            Odmítnout volitelné
          </button>
        </div>
      </div>
    </div>
  );
}

function CookieRow({ icon, title, text, checked, onChange, disabled, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <label className="flex items-start gap-3 cursor-pointer">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-300/10 border border-amber-300/20 text-amber-300">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="font-heading text-sm font-bold text-white">{title}</span>
            <Toggle
              checked={checked}
              disabled={disabled}
              onChange={(v) => onChange && onChange(v)}
              testid={testid}
            />
          </div>
          <p className="mt-1 text-xs text-slate-400 leading-relaxed">{text}</p>
        </div>
      </label>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={[
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-amber-500" : "bg-slate-700",
        disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
    >
      <span
        className={[
          "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
          checked ? "translate-x-5" : "translate-x-0.5",
        ].join(" ")}
      />
    </button>
  );
}
